import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectJourneyContext,
  selectShowSkipConfirmation,
  setShowSkipConfirmation,
  skipOnboarding,
} from '../reducers/onboardingSlice';
import OnboardingProgress from './OnboardingProgress';
import ContactSupportButton from 'components/ContactSupportButton';

/**
 * Props for the {@link OnboardingLayout} component
 */
interface OnboardingLayoutProps {
  /** The content to display within the layout */
  children: React.ReactNode;
  /** The current onboarding step being displayed */
  currentStep: 'snowflake_setup' | 'runner_provision';
}

/**
 * OnboardingLayout Component
 *
 * Provides the full-screen layout wrapper for all onboarding steps with enhanced design.
 * Features animated Snowflake + DataOps sync icon, progress indicator, and skip functionality
 * with confirmation dialog.
 *
 * @component
 * @example
 * ```tsx
 * <OnboardingLayout currentStep="snowflake_setup">
 *   <SnowflakeAccountFormV2 />
 * </OnboardingLayout>
 * ```
 *
 * @param props - Component props
 * @param props.children - The step content to display
 * @param props.currentStep - The current onboarding step
 * @returns Full-screen onboarding layout with header, progress, and content
 */
const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({ children, currentStep }) => {
  const dispatch = useAppDispatch();
  const journeyContext = useAppSelector(selectJourneyContext);
  const showSkipConfirmation = useAppSelector(selectShowSkipConfirmation);

  /**
   * Handles the skip button click by showing the confirmation dialog
   */
  const handleSkipClick = (): void => {
    dispatch(setShowSkipConfirmation(true));
  };

  /**
   * Handles confirmation of skip action and closes the dialog
   */
  const handleConfirmSkip = (): void => {
    dispatch(skipOnboarding());
    dispatch(setShowSkipConfirmation(false));
  };

  /**
   * Handles cancellation of skip action and closes the dialog
   */
  const handleCancelSkip = (): void => {
    dispatch(setShowSkipConfirmation(false));
  };

  return (
    <div className="min-h-screen bg-dashboard-background">
      <div className="relative bg-dataops-dark pb-32">
        <header className="py-8 sm:py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
              {journeyContext?.app_name ?? 'Onboarding'}
            </h1>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
            >
              <button
                type="button"
                onClick={handleSkipClick}
                className="rounded-full bg-dataops-secondary-blue px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-hover-secondary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all duration-150"
                aria-label="Skip onboarding setup for now and go to dashboard"
              >
                Skip for now
              </button>
            </motion.div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <OnboardingProgress currentStep={currentStep} />
        </div>
      </div>

      <main className="relative -mt-24" role="main">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {children}
        </motion.div>
      </main>

      <ContactSupportButton />

      {/* Skip Confirmation Dialog */}
      <Transition.Root show={showSkipConfirmation} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCancelSkip}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h2" className="text-lg  leading-6 text-dataops-dark">
                        Skip onboarding?
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-secondary-text">
                          You can set up your Snowflake account later, but you won&apos;t be able to use the platform
                          until it&apos;s configured. Are you sure you want to skip this step?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-dataops-primary-blue px-4 py-2.5 text-sm  text-white shadow-sm hover:bg-hover-primary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue sm:col-start-2 transition-colors duration-150"
                      onClick={handleCancelSkip}
                      aria-label="Continue with onboarding setup"
                      autoFocus
                    >
                      Continue setup
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm  text-primary-text shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue sm:col-start-1 sm:mt-0 transition-colors duration-150"
                      onClick={handleConfirmSkip}
                      aria-label="Skip onboarding and go to dashboard"
                    >
                      Skip for now
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default OnboardingLayout;
